import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Alert,
  message,
  Card,
  Divider,
  Select,
  Tag,
  Checkbox,
} from 'antd';
import { 
  MailOutlined, 
  DeleteOutlined, 
  EditOutlined, 
  SendOutlined,
  FileTextOutlined 
} from '@ant-design/icons';
import api from '../../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const { Option } = Select;

const statusColors = {
  submitted: 'blue',
  reviewed: 'purple',
  interviewed: 'orange',
  hired: 'green',
  rejected: 'red'
};

const ApplicationsManagement = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [emailVisible, setEmailVisible] = useState(false);
  const [bulkEmailVisible, setBulkEmailVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [form] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [bulkEmailContent, setBulkEmailContent] = useState('');

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/career-applications');
      setApplications(data);
    } catch (err) {
      setErrorMsg('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setVisible(true);
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      job_id: record.job_id,
      job_title: record.job_title,
      applicant_name: record.applicant_name,
      applicant_email: record.applicant_email,
      applicant_phone: record.applicant_phone,
      resume_link: record.resume_link,
      cover_letter: record.cover_letter,
      status: record.status
    });
    setEditingId(record.id);
    setVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete this application?',
      content: 'This will permanently remove the application record.',
      onOk: async () => {
        try {
          await api.delete(`/career-applications/${id}`);
          fetchApplications();
          message.success('Application deleted successfully');
        } catch {
          message.error('Failed to delete application');
        }
      },
    });
  };

  const handleSendEmail = (application) => {
    setSelectedApplication(application);
    emailForm.resetFields();
    setEmailContent('');
    setEmailVisible(true);
  };

  const handleSendBulkEmail = () => {
    emailForm.resetFields();
    setBulkEmailContent('');
    setBulkEmailVisible(true);
  };

  const handleRowSelection = (selectedRowKeys, selectedRows) => {
    setSelectedRows(selectedRows);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingId) {
        await api.put(`/career-applications/${editingId}`, values);
        setSuccessMsg('Application updated successfully');
      } else {
        await api.post('/career-applications', values);
        setSuccessMsg('Application added successfully');
      }
      
      setVisible(false);
      fetchApplications();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to save application');
    }
  };

  const handleEmailSubmit = async () => {
    try {
      const values = await emailForm.validateFields();
      
      if (selectedApplication) {
        await api.post(`/career-applications/send-email/${selectedApplication.id}`, {
          subject: values.subject,
          message: emailContent,
        });
        message.success('Email sent successfully');
      }
      
      setEmailVisible(false);
    } catch (err) {
      message.error('Failed to send email');
    }
  };

  const handleBulkEmailSubmit = async () => {
    try {
      const values = await emailForm.validateFields();
      
      const applicationIds = selectedRows.map(row => row.id);
      
      await api.post('/career-applications/send-bulk-email', {
        subject: values.subject,
        message: bulkEmailContent,
        applicationIds: applicationIds.length > 0 ? applicationIds : null
      });
      
      const count = applicationIds.length > 0 ? applicationIds.length : applications.length;
      message.success(`Email sent to ${count} applicants`);
      setBulkEmailVisible(false);
      setSelectedRows([]);
    } catch (err) {
      message.error('Failed to send bulk email');
    }
  };

  const columns = [
    {
      title: 'Select',
      render: (_, record) => (
        <Checkbox 
          checked={selectedRows.some(row => row.id === record.id)}
          onChange={e => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, record]);
            } else {
              setSelectedRows(selectedRows.filter(row => row.id !== record.id));
            }
          }}
        />
      ),
    },
    { 
      title: 'Job ID', 
      dataIndex: 'job_id', 
      key: 'job_id',
      sorter: (a, b) => a.job_id - b.job_id,
    },
    { 
      title: 'Job Title', 
      dataIndex: 'job_title', 
      key: 'job_title',
      sorter: (a, b) => a.job_title.localeCompare(b.job_title),
    },
    { 
      title: 'Applicant Name', 
      dataIndex: 'applicant_name', 
      key: 'applicant_name',
      sorter: (a, b) => a.applicant_name.localeCompare(b.applicant_name),
    },
    { 
      title: 'Email', 
      dataIndex: 'applicant_email', 
      key: 'applicant_email',
      sorter: (a, b) => a.applicant_email.localeCompare(b.applicant_email),
    },
     { 
    title: 'Resume', 
    key: 'resume_link',
    render: (_, record) => (
      <Button 
        type="link" 
        onClick={() => window.open(record.resume_link, '_blank')}
        icon={<FileTextOutlined />}
      >
        View Resume
      </Button>
    ),
  },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      render: status => (
        <Tag color={statusColors[status]}>
          {status.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Submitted', value: 'submitted' },
        { text: 'Reviewed', value: 'reviewed' },
        { text: 'Interviewed', value: 'interviewed' },
        { text: 'Hired', value: 'hired' },
        { text: 'Rejected', value: 'rejected' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    { 
      title: 'Submitted At', 
      dataIndex: 'submitted_at', 
      key: 'submitted_at',
      render: date => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.submitted_at) - new Date(b.submitted_at),
    },
  
    {
  title: 'Actions',
  key: 'actions',
  render: (_, record) => (
    <Space>
      <Button 
        icon={<EditOutlined />} 
        onClick={() => handleEdit(record)}
      >
        Edit
      </Button>
      <Button 
        type="primary" 
        icon={<FileTextOutlined />} 
        onClick={() => window.open(record.resume_link, '_blank')}
      >
        View Resume
      </Button>
      <Button 
        icon={<MailOutlined />} 
        onClick={() => handleSendEmail(record)}
      >
        Email
      </Button>
      <Button 
        danger 
        icon={<DeleteOutlined />} 
        onClick={() => handleDelete(record.id)}
      >
        Delete
      </Button>
    </Space>
  ),
},
  ];

  return (
    <div>
      <h2>Career Applications</h2>

      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleAdd}>Add Application</Button>
        <Button 
          type="primary" 
          icon={<SendOutlined />} 
          onClick={handleSendBulkEmail}
          disabled={applications.length === 0}
        >
          {selectedRows.length > 0 ? 
            `Send Email to ${selectedRows.length} Selected` : 
            'Send Email to All'}
        </Button>
      </Space>

      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          closable
          onClose={() => setSuccessMsg('')}
          style={{ marginBottom: 12 }}
        />
      )}

      {errorMsg && (
        <Alert
          message={errorMsg}
          type="error"
          showIcon
          closable
          onClose={() => setErrorMsg('')}
          style={{ marginBottom: 12 }}
        />
      )}

     <Card 
      title={`Applications (${applications.length})`}
      style={{
        overflowX: 'auto',
        width: '100%'
      }}
    >
      <div style={{ minWidth: '800px' }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={applications}
          loading={loading}
          pagination={{ pageSize: 10 }}
          rowSelection={{
            selectedRowKeys: selectedRows.map(row => row.id),
            onChange: handleRowSelection,
          }}
          scroll={{ x: true }}
          style={{
            width: '100%',
            minWidth: '100%'
          }}
        />
      </div>
    </Card>

      {/* Add/Edit Application Modal */}
      <Modal
        title={editingId ? 'Edit Application' : 'Add Application'}
        visible={visible}
        onOk={handleSubmit}
        onCancel={() => setVisible(false)}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="job_id"
            label="Job ID"
            rules={[{ required: true, message: 'Please input job ID!' }]}
          >
            <Input type="number" />
          </Form.Item>
          
          <Form.Item
            name="job_title"
            label="Job Title"
            rules={[{ required: true, message: 'Please input job title!' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="applicant_name"
            label="Applicant Name"
            rules={[{ required: true, message: 'Please input applicant name!' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="applicant_email"
            label="Applicant Email"
            rules={[
              { required: true, message: 'Please input email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="applicant_phone"
            label="Applicant Phone"
            rules={[{ required: true, message: 'Please input phone number!' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="resume_link"
            label="Resume Link"
            rules={[{ required: true, message: 'Please input resume link!' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="cover_letter"
            label="Cover Letter"
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status!' }]}
          >
            <Select>
              <Option value="submitted">Submitted</Option>
              <Option value="reviewed">Reviewed</Option>
              <Option value="interviewed">Interviewed</Option>
              <Option value="hired">Hired</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Send Email Modal */}
      <Modal
        title={`Send Email to ${selectedApplication?.applicant_name || 'Applicant'}`}
        visible={emailVisible}
        onOk={handleEmailSubmit}
        onCancel={() => setEmailVisible(false)}
        width={800}
      >
        <Form form={emailForm} layout="vertical">
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please input subject!' }]}
          >
            <Input />
          </Form.Item>
          <Divider />
          <h4>Email Content</h4>
          <ReactQuill 
            theme="snow" 
            value={emailContent} 
            onChange={setEmailContent} 
            style={{ height: '300px', marginBottom: '40px' }}
          />
        </Form>
      </Modal>

      {/* Send Bulk Email Modal */}
      <Modal
        title={
          selectedRows.length > 0 ? 
            `Send Email to ${selectedRows.length} Selected Applicants` : 
            `Send Email to All Applicants (${applications.length})`
        }
        visible={bulkEmailVisible}
        onOk={handleBulkEmailSubmit}
        onCancel={() => setBulkEmailVisible(false)}
        width={800}
      >
        <Form form={emailForm} layout="vertical">
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please input subject!' }]}
          >
            <Input />
          </Form.Item>
          <Divider />
          <h4>Email Content</h4>
          <ReactQuill 
            theme="snow" 
            value={bulkEmailContent} 
            onChange={setBulkEmailContent} 
            style={{ height: '300px', marginBottom: '40px' }}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default ApplicationsManagement;